import { useEffect, useState } from 'react'
import useMediaQuery from '../../../../hooks/use-media-query'
import CourseDropdown from '../CourseDropdown'
import StudyTime from '../StudyTime'
import DashboardCarousel from '../DashboardCarousel'
import ModulesChart from '../ModulesChart'
import DashboardTooltip from '../DashboardTooltip'
import Empty from './Empty'
import {
  CarouselWrapper,
  Container,
  Content,
  ModulosWrapper,
  UpperContent,
  StudyTimeWrapper,
} from './styles'
import Loading from './Loading'
import { useGetCourses } from '../../hooks/use-get-courses'
import { IDashboardCourse } from './types'
import { useGetGeneral } from '../../hooks/use-get-general'
import { useGetProgress } from '../../hooks/use-get-progress'
import { useGetStudyTime } from '../../hooks/use-get-study-time'
import { useGetPreferences } from '../../hooks/use-get-preferences'
import { useDashboardTracking } from '../../hooks/use-dashboard-tracking'
import Error from './Error'
import NoData from './NoData'

const Dashboard = () => {
  const tracking = useDashboardTracking()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const [selectedCourse, setSelectedCourse] = useState<IDashboardCourse | null>(null)

  const preferences = useGetPreferences()

  const coursesSWR = useGetCourses()
  const generalSWR = useGetGeneral(selectedCourse?._id)
  const progressSWR = useGetProgress(selectedCourse?._id)
  const studyTimeSWR = useGetStudyTime(selectedCourse?._id)

  const isLoading =
    coursesSWR.isLoading ||
    generalSWR.isLoading ||
    progressSWR.isLoading ||
    studyTimeSWR.isLoading ||
    preferences.loading

  const hasError =
    !!coursesSWR.error || !!generalSWR.error || !!progressSWR.error || !!studyTimeSWR.error

  const refetch = () => {
    generalSWR.mutate()
    progressSWR.mutate()
    studyTimeSWR.mutate()
  }

  const onSelect = (course: IDashboardCourse) => {
    setSelectedCourse(course)

    tracking.onSelectCourse({ courseId: course._id, courseName: course.name })

    if (hasError) refetch()
  }

  const renderContent = () => {
    if (isLoading) return <Loading />

    if (hasError) return <Error />

    if (coursesSWR.data.length === 0) return <NoData />

    if (!selectedCourse) return <Empty />

    return (
      <>
        <CarouselWrapper>
          <div className="tooltip-wrapper">
            <h2 className="title">Análise Geral</h2>

            <DashboardTooltip>Principais métricas do seu desempenho</DashboardTooltip>
          </div>

          <DashboardCarousel progress={progressSWR.data} general={generalSWR.data} />
        </CarouselWrapper>

        <StudyTimeWrapper>
          <div className="tooltip-wrapper">
            <h2 className="title">Tempo de estudo</h2>

            <DashboardTooltip>Quantidade de horas de estudo por dia</DashboardTooltip>
          </div>
          <StudyTime timeStudy={studyTimeSWR.data} course={selectedCourse} tracking={tracking} />
        </StudyTimeWrapper>

        <ModulosWrapper>
          <div className="tooltip-wrapper">
            <h2 className="title">{isDesktop ? 'Módulos mais vistos' : 'Módulos'}</h2>
            <DashboardTooltip horizontalOffset={isDesktop ? 0 : 50}>
              Veja o quanto você progrediu através dos módulos
            </DashboardTooltip>
          </div>
          <ModulesChart progress={progressSWR.data} />
        </ModulosWrapper>
      </>
    )
  }

  useEffect(() => {
    if (coursesSWR.data.length === 1) {
      const firstCourse = coursesSWR.data[0]

      setSelectedCourse(firstCourse)

      tracking.onSelectCourse({
        courseId: firstCourse._id,
        courseName: firstCourse.name,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coursesSWR.data])

  return (
    <Container>
      <Content>
        <UpperContent>
          <div>
            <h1 className="title">Seu Desempenho</h1>
            <p className="description">
              Acompanhe as métricas dos seus estudos e sua evolução
              {selectedCourse && (
                <>
                  {' '}
                  no <b>{selectedCourse.name}</b>
                </>
              )}
              .
            </p>
          </div>

          <div className="dropdown-wrapper">
            <p className="title">Trocar Curso</p>
            <CourseDropdown
              value={selectedCourse?._id}
              onChange={onSelect}
              courses={coursesSWR.data}
              disabled={coursesSWR.data.length === 1}
              loading={coursesSWR.isLoading}
            />
          </div>
        </UpperContent>

        {renderContent()}
      </Content>
    </Container>
  )
}

export default Dashboard
